/**
 * @zendir/ui - Grid Layout Primitive
 *
 * CSS Grid container with responsive columns and auto-fit support.
 * Designed for operator dashboard layouts: telemetry grids, card layouts,
 * multi-panel mission control views.
 *
 * Astro UX Compliance:
 * - 4px grid spacing tokens
 * - Responsive column count for mobile → 4K mission control
 *
 * @example
 * ```tsx
 * // Fixed responsive columns
 * <Grid columns={[1, 2, 3]} gap="md">
 *   <TelemetryCard />
 *   <LinkBudgetCard />
 *   <SpacecraftCard />
 * </Grid>
 *
 * // Auto-fit: as many 300px-min columns as fit
 * <Grid autoFit={300} gap="md">
 *   {telemetryItems.map(item => <DataValue key={item.key} {...item} />)}
 * </Grid>
 *
 * // Template-based for complex layouts
 * <Grid template="1fr 2fr 1fr" rows="auto 1fr" gap="md">
 *   <SideNav />
 *   <MainContent />
 *   <DetailPanel />
 * </Grid>
 * ```
 */

import React, { memo, forwardRef } from "react";
import { useTheme } from "../../theme";
import { useBreakpoint } from "./useBreakpoint";
import { resolveResponsiveSpacing, resolveResponsive } from "./responsive";
import type { ResponsiveValue } from "./useBreakpoint";
import type { SpacingToken } from "./responsive";
import type { BoxProps } from "./Box";

type SpacingInput = SpacingToken | string | number;

export interface GridProps extends Omit<BoxProps, "display"> {
  /** Number of equal columns (responsive) */
  columns?: ResponsiveValue<number>;
  /** Auto-fit: minimum column width in px. Creates `repeat(auto-fit, minmax(Npx, 1fr))` */
  autoFit?: number;
  /** Auto-fill: minimum column width in px. Creates `repeat(auto-fill, minmax(Npx, 1fr))` */
  autoFill?: number;
  /** Explicit grid-template-columns CSS value */
  template?: ResponsiveValue<string>;
  /** Explicit grid-template-rows CSS value */
  rows?: string;
  /** Gap between grid items */
  gap?: ResponsiveValue<SpacingInput>;
  /** Row gap (overrides gap for rows) */
  rowGap?: ResponsiveValue<SpacingInput>;
  /** Column gap (overrides gap for columns) */
  columnGap?: ResponsiveValue<SpacingInput>;
  /** Align items (cross-axis per cell) */
  align?: "start" | "end" | "center" | "stretch" | "baseline";
  /** Justify items (main-axis per cell) */
  justifyItems?: "start" | "end" | "center" | "stretch";
  /** Align content (cross-axis for the grid as a whole) */
  alignContent?:
    | "start"
    | "end"
    | "center"
    | "stretch"
    | "space-between"
    | "space-around"
    | "space-evenly";
  /** Justify content (main-axis for the grid as a whole) */
  justifyContent?:
    | "start"
    | "end"
    | "center"
    | "stretch"
    | "space-between"
    | "space-around"
    | "space-evenly";
  /** Make grid inline */
  inline?: boolean;
}

export const Grid = memo(
  forwardRef<HTMLDivElement, GridProps>(function Grid(
    {
      columns,
      autoFit,
      autoFill,
      template,
      rows,
      gap,
      rowGap,
      columnGap,
      align,
      justifyItems,
      alignContent,
      justifyContent,
      inline = false,
      // Box props
      padding,
      p,
      px,
      py,
      pt,
      pr,
      pb,
      pl,
      margin,
      m,
      mx,
      my,
      mt,
      mr,
      mb,
      ml,
      background,
      border,
      borderRadius,
      shadow,
      width,
      height,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight,
      overflow,
      position,
      flex,
      flexGrow,
      flexShrink,
      as: Component = "div",
      style,
      children,
      ...rest
    },
    ref,
  ): React.ReactElement {
    const { tokens } = useTheme();
    const { breakpoint } = useBreakpoint();

    const resolveSpacing = (val: ResponsiveValue<SpacingInput> | undefined) =>
      resolveResponsiveSpacing(val, breakpoint, tokens);

    // Determine grid-template-columns
    let gridTemplateColumns: string | undefined;

    if (autoFit) {
      gridTemplateColumns = `repeat(auto-fit, minmax(${autoFit}px, 1fr))`;
    } else if (autoFill) {
      gridTemplateColumns = `repeat(auto-fill, minmax(${autoFill}px, 1fr))`;
    } else if (template) {
      gridTemplateColumns = resolveResponsive(template, breakpoint);
    } else if (columns !== undefined) {
      const cols = resolveResponsive(columns, breakpoint);
      gridTemplateColumns = cols ? `repeat(${cols}, 1fr)` : undefined;
    }

    // Background resolution
    const bgMap: Record<string, string> = {
      base: tokens.colors.background.base,
      surface: tokens.colors.background.surface,
      elevated: tokens.colors.background.elevated,
      overlay: tokens.colors.background.overlay,
    };

    const resolveSize = (
      val: string | number | undefined,
    ): string | undefined => {
      if (val === undefined) return undefined;
      if (typeof val === "number") return `${val}px`;
      return val;
    };

    // Resolve padding
    const paddingAll = resolveSpacing(padding ?? p);
    const paddingXVal = resolveSpacing(px);
    const paddingYVal = resolveSpacing(py);

    // Resolve margin
    const marginAll = resolveSpacing(margin ?? m);
    const marginXVal = resolveSpacing(mx);
    const marginYVal = resolveSpacing(my);

    // Resolve gap values — use conditional spreads to avoid setting
    // rowGap/columnGap to undefined, which clears the gap shorthand
    // in browsers (setting `style.rowGap = ''` removes the value
    // that `style.gap` set for row-gap).
    const resolvedGap = resolveSpacing(gap);
    const resolvedRowGap = resolveSpacing(rowGap);
    const resolvedColumnGap = resolveSpacing(columnGap);

    const computedStyle: React.CSSProperties = {
      display: inline ? "inline-grid" : "grid",
      gridTemplateColumns,
      gridTemplateRows: rows,
      ...(resolvedGap !== undefined ? { gap: resolvedGap } : {}),
      ...(resolvedRowGap !== undefined ? { rowGap: resolvedRowGap } : {}),
      ...(resolvedColumnGap !== undefined
        ? { columnGap: resolvedColumnGap }
        : {}),
      alignItems: align,
      justifyItems,
      alignContent,
      justifyContent,

      // Padding
      ...(paddingAll ? { padding: paddingAll } : {}),
      ...(paddingXVal
        ? { paddingLeft: paddingXVal, paddingRight: paddingXVal }
        : {}),
      ...(paddingYVal
        ? { paddingTop: paddingYVal, paddingBottom: paddingYVal }
        : {}),
      ...(resolveSpacing(pt) ? { paddingTop: resolveSpacing(pt) } : {}),
      ...(resolveSpacing(pr) ? { paddingRight: resolveSpacing(pr) } : {}),
      ...(resolveSpacing(pb) ? { paddingBottom: resolveSpacing(pb) } : {}),
      ...(resolveSpacing(pl) ? { paddingLeft: resolveSpacing(pl) } : {}),

      // Margin
      ...(marginAll ? { margin: marginAll } : {}),
      ...(marginXVal
        ? { marginLeft: marginXVal, marginRight: marginXVal }
        : {}),
      ...(marginYVal
        ? { marginTop: marginYVal, marginBottom: marginYVal }
        : {}),
      ...(resolveSpacing(mt) ? { marginTop: resolveSpacing(mt) } : {}),
      ...(resolveSpacing(mr) ? { marginRight: resolveSpacing(mr) } : {}),
      ...(resolveSpacing(mb) ? { marginBottom: resolveSpacing(mb) } : {}),
      ...(resolveSpacing(ml) ? { marginLeft: resolveSpacing(ml) } : {}),

      // Visual
      ...(background
        ? { backgroundColor: bgMap[background] || background }
        : {}),
      ...(border
        ? {
            border:
              border === true
                ? `1px solid ${tokens.colors.border.default}`
                : (border as string),
          }
        : {}),
      ...(borderRadius
        ? {
            borderRadius:
              (tokens.borderRadius as Record<string, string>)[
                borderRadius as string
              ] || borderRadius,
          }
        : {}),
      ...(shadow && shadow !== "glow"
        ? {
            boxShadow:
              (tokens.shadows as unknown as Record<string, string>)[
                shadow as string
              ] || shadow,
          }
        : {}),

      // Size
      width: resolveSize(resolveResponsive(width, breakpoint)),
      height: resolveSize(resolveResponsive(height, breakpoint)),
      minWidth: resolveSize(minWidth),
      minHeight: resolveSize(minHeight),
      maxWidth: resolveSize(maxWidth),
      maxHeight: resolveSize(maxHeight),
      overflow,
      position,
      flex,
      flexGrow,
      flexShrink,

      ...style,
    };

    return (
      <Component ref={ref} style={computedStyle} {...rest}>
        {children}
      </Component>
    );
  }),
);

export default Grid;
